"use client";

import { analytics } from "@analytics";
import { useRouter } from "next/navigation";

import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";

export type ImportOrigin =
  | "CollectionsEmptyState"
  | "ActionMenuLinkWallet"
  | "ExternalWallets"
  | "ActionMenu"
  | "CollectionCard"
  | "ImportSuccess"
  | "ItemDetails";

export type ActiveAssetImport = {
  importAddress: string;
  trackingId: string;
};

export const AssetImportContext = createContext<{
  activeImport: ActiveAssetImport | undefined;
  startImport:
    | ((
        url: { pathname?: string } | undefined,
        importAddress: string,
        importOrigin: ImportOrigin,
        forceNewImport: boolean,
      ) => void)
    | undefined;
  endImport: (() => void) | undefined;
} | null>(null);

const useAssetImport = () => {
  const ctx = useContext(AssetImportContext);
  if (!ctx) {
    throw new Error("useAssetImport must be used within a AssetImportProvider");
  }
  return ctx;
};

const AssetImportProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const [activeImport, setActiveImport] = useState<
    ActiveAssetImport | undefined
  >(undefined);
  const startImport = useCallback(
    (
      url: { pathname?: string } | undefined,
      importAddress: string,
      importOrigin: ImportOrigin,
      forceNewImport = false,
    ) => {
      // If the import is already active, don't start a new one e.g. click import on collection card
      if (
        !activeImport ||
        activeImport.importAddress !== importAddress ||
        forceNewImport
      ) {
        const newTrackingId = uuidv4();
        setActiveImport({ importAddress, trackingId: newTrackingId });
        analytics.track("PassportDashboardAssetTransfer_Started", {
          flowId: newTrackingId,
          importOrigin,
        });
      }

      if (url?.pathname) {
        router.push(url.pathname);
      }
    },
    [router, activeImport],
  );
  const endImport = useCallback(() => {
    setActiveImport(undefined);
  }, []);
  return (
    <AssetImportContext.Provider
      value={{
        activeImport,
        startImport,
        endImport,
      }}
    >
      {children}
    </AssetImportContext.Provider>
  );
};
export { AssetImportProvider, useAssetImport };
